import { LineItem, PVWireItem, SingleItemTotals, MaterialTotals } from '../types';
import { parseSafeNumber, fixFractionFormat } from '../utils/helpers';

export function getInverterCountFromString(text?: string): number {
  const lowerText = (text || '').toLowerCase();
  if (!lowerText.includes('inverter') && !lowerText.includes('inv')) {
    return 1;
  }

  const rangeRegex = /#?(\d+)\s*(?:-|to|thru)\s*#?(\d+)/;
  const rangeMatch = lowerText.match(rangeRegex);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    if (!isNaN(start) && !isNaN(end) && end >= start) {
      return end - start + 1;
    }
  }

  const listRegex = /#?(\d+)(?:\s*,\s*#?(\d+))+/;
  const listMatch = lowerText.match(listRegex);
  if (listMatch) {
    const numbers = lowerText.match(/\d+/g);
    if (numbers) {
      const uniqueNums = Array.from(new Set(numbers.map((n) => parseInt(n, 10))));
      return uniqueNums.length;
    }
  }

  const qtyRegexes = [
    /(?:qty|quantity|x|\()\s*(\d+)\s*\)?\s*inv(?:erter)?/,
    /inv(?:erter)?[s]?\s*(?:\(?qty|quantity|x|\()\s*:?\s*(\d+)\)?/,
    /(\d+)\s*(?:x|qty)?\s*inv(?:erter)?/,
  ];
  for (const regex of qtyRegexes) {
    const match = lowerText.match(regex);
    if (match) {
      const qty = parseInt(match[1], 10);
      if (!isNaN(qty) && qty > 0) {
        return qty;
      }
    }
  }

  return 1;
}

export function calculateSingleItemTotals(item: LineItem): SingleItemTotals {
  const length = parseSafeNumber(item['Max_Approx_Length']);
  const tradeSize = item['Trade_Size'] || '';
  const wireQuantity = parseSafeNumber(item['Qty_Wire']);
  const rawGroundSize = item['Equipment_Grounding_Conductor_Size'] || '';
  const groundSizeNormalized = rawGroundSize.toLowerCase().trim();
  const conduitType = item['Conduit_Type'] || '';
  const conduitSize = fixFractionFormat(item['Conduit_Size'] || '');
  const neutral = (item['Neutral'] || '').toLowerCase().trim();

  let parallel = parseSafeNumber(item['Qty_Parallel']);
  if (parallel < 1) parallel = 1;

  const hasNeutral =
    neutral !== '' &&
    neutral !== 'none' &&
    neutral !== 'no' &&
    neutral !== 'n' &&
    (neutral.includes('yes') || neutral.includes('y') || neutral.includes('neutral') || /\d/.test(neutral));
  const neutralAdder = hasNeutral ? 1 : 0;

  const hasGround =
    groundSizeNormalized !== '' &&
    groundSizeNormalized !== 'none' &&
    groundSizeNormalized !== 'no' &&
    groundSizeNormalized !== 'n' &&
    (groundSizeNormalized.includes('yes') ||
      groundSizeNormalized.includes('y') ||
      groundSizeNormalized.includes('ground') ||
      /\d/.test(groundSizeNormalized));
  const groundAdder = hasGround ? 1 : 0;

  let wireTotal = (wireQuantity + neutralAdder) * length * parallel;
  let groundTotal = groundAdder * length * parallel;
  let conduitLength = length * parallel;

  const circuitOrigin = item['Circuit_Origin'] || '';
  const circuitDestination = item['Circuit_Destination'] || '';

  const containsInverter = (text: string) => {
    const lower = text.toLowerCase();
    return lower.includes('inverter') || lower.includes('inv');
  };

  let inverterMultiplier = 1;
  if (containsInverter(circuitOrigin) || containsInverter(circuitDestination)) {
    inverterMultiplier = getInverterCountFromString(circuitOrigin);
    if (inverterMultiplier === 1) {
      inverterMultiplier = getInverterCountFromString(circuitDestination);
    }
    wireTotal *= inverterMultiplier;
    groundTotal *= inverterMultiplier;
    conduitLength *= inverterMultiplier;
  }

  return {
    length,
    tradeSize,
    wireQuantity,
    groundSize: rawGroundSize,
    conduitType,
    conduitSize,
    parallel,
    neutralAdder,
    groundAdder,
    inverterMultiplier,
    wireTotal,
    groundTotal,
    conduitLength,
  };
}

export function calculateTotals(
  lineItems: LineItem[],
  pvWireItems: PVWireItem[] = []
): MaterialTotals {
  const wireHashMap: Record<string, number> = {};
  const conduitHashMap: Record<string, number> = {};

  for (const item of lineItems) {
    const ac_dc = (item['AC_DC'] || '').toLowerCase();
    const origin = (item['Circuit_Origin'] || '').toLowerCase();
    const dest = (item['Circuit_Destination'] || '').toLowerCase();
    if (ac_dc.includes('dc') || (origin.includes('module') && dest.includes('inverter'))) {
      continue;
    }

    const res = calculateSingleItemTotals(item);
    if (res.length === 0 || res.tradeSize === '') {
      continue;
    }

    wireHashMap[res.tradeSize] = (wireHashMap[res.tradeSize] || 0) + res.wireTotal;

    if (res.groundAdder === 1 && res.groundSize) {
      wireHashMap[res.groundSize] = (wireHashMap[res.groundSize] || 0) + res.groundTotal;
    }

    if (res.conduitSize && res.conduitType) {
      const conduitKey = `${res.conduitSize}" ${res.conduitType}`;
      conduitHashMap[conduitKey] = (conduitHashMap[conduitKey] || 0) + res.conduitLength;
    }
  }

  if (pvWireItems && pvWireItems.length > 0) {
    for (const item of pvWireItems) {
      const distance = parseSafeNumber(item['max_distance_ft']);
      const totalForString = distance * 2;
      if (totalForString > 0) {
        const wireSize = item['pv_wire_size'] || item['Wire Size'] || '#10 AWG Cu';
        wireHashMap[wireSize] = (wireHashMap[wireSize] || 0) + totalForString;
      }
    }
  }

  return { wireHashMap, conduitHashMap };
}
